import type { ClientMsg, ServerMsg } from '@infiltre/shared';

export const DriverMode = {
  Online: 'online',
  Standalone: 'standalone',
} as const;
export type DriverMode = (typeof DriverMode)[keyof typeof DriverMode];

export const DriverStatusKind = {
  Idle: 'idle',
  Connecting: 'connecting',
  /**
   * First connect attempt. `Reconnecting` is a separate state because UI
   * cares about "this is a reconnect after a drop" vs "this is the first
   * connection" — the former is unexpected and surfaces a banner; the
   * latter is expected and stays quiet.
   */
  Reconnecting: 'reconnecting',
  Ready: 'ready',
  Error: 'error',
  Unauthorized: 'unauthorized',
  RoomNotFound: 'room_not_found',
  RoomFull: 'room_full',
  /** The presence service kicked us after the in-game/lobby grace expired. */
  KickedDisconnected: 'kicked_disconnected',
} as const;
export type DriverStatusKind = (typeof DriverStatusKind)[keyof typeof DriverStatusKind];

export type DriverStatus =
  | { kind: typeof DriverStatusKind.Idle }
  | { kind: typeof DriverStatusKind.Connecting }
  | { kind: typeof DriverStatusKind.Reconnecting; attempt: number }
  | { kind: typeof DriverStatusKind.Ready }
  | { kind: typeof DriverStatusKind.Error; reason: string }
  | { kind: typeof DriverStatusKind.Unauthorized }
  | { kind: typeof DriverStatusKind.RoomNotFound }
  | { kind: typeof DriverStatusKind.RoomFull }
  | { kind: typeof DriverStatusKind.KickedDisconnected };

export type Unsubscribe = () => void;

export interface GameDriver {
  readonly mode: DriverMode;
  status(): DriverStatus;
  start(): Promise<void>;
  stop(): Promise<void>;
  send(msg: ClientMsg): void;
  onMessage(handler: (m: ServerMsg) => void): Unsubscribe;
  onStatus(handler: (s: DriverStatus) => void): Unsubscribe;
}
